import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { organDonorService } from '../../../../services/organDonorInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('OrganDonorIntroScreen3');

export const OrganDonorIntroScreen3: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();

  const updateEducationPreference = async (wantsEducation: boolean) => {
    try {
      // Get existing record or create new one, then update with education preference
      const allRecords = await organDonorService.getAllOrganDonor();
      if (allRecords.length > 0) {
        // Update existing record with notes about education preference
        await organDonorService.updateOrganDonor(allRecords[0].id, {
          notes: allRecords[0].notes ?
            `${allRecords[0].notes}; Wants education: ${wantsEducation}` :
            `Wants education about organ donation: ${wantsEducation}`
        });
      } else {
        // Create new record with education preference
        await organDonorService.createOrganDonor({
          notes: `Wants education about organ donation: ${wantsEducation}`,
          taskId: 12,
        });
      }
    } catch (error) {
      logger.error('Error updating education preference:', error);
    }
  };

  const handleYes = async () => {
    await updateEducationPreference(true);
    navigation.navigate('OrganDonorIntro4');
  };

  const handleNo = async () => {
    await updateEducationPreference(false);
    navigation.navigate('OrganDonorInput');
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <View style={styles.content}>
          <Text style={styles.title}>Would you like to learn more about the organ donation process?</Text>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.yesButton]}
              onPress={handleYes}
            >
              <Text style={[styles.buttonText, styles.yesText]}>Yes, tell me more</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, styles.noButton]}
              onPress={handleNo}
            >
              <Text style={[styles.buttonText, styles.noText]}>No, I'm ready to record my wishes</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.description}>
            Don’t let hesitation stop you from saving a life. Saying “yes” to organ donation takes only a moment, but it can change the future for families and recipients while leaving a lasting legacy. If you’ve considered it, now is the time to decide—someone’s life may depend on it.
          </Text>
        </View>
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  content: {
    flex: 1,
    padding: ms(20),
    alignItems: 'center',
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: '600',
    color: '#2C4364',
    marginBottom: ms(30),
    textAlign: 'center',
    paddingHorizontal: ms(20),
  },
  description: {
    fontSize: ms(18, 0.3),
    color: '#666666',
    textAlign: 'center',
    marginTop: ms(30),
    paddingHorizontal: ms(20),
    lineHeight: ms(25, 0.3),
  },
  buttonContainer: {
    width: '100%',
    paddingHorizontal: ms(20),
  },
  button: {
    width: '100%',
    borderRadius: ms(30),
    padding: ms(15),
    alignItems: 'center',
    marginBottom: ms(15),
  },
  yesButton: {
    backgroundColor: '#29BFC0',
  },
  noButton: {
    backgroundColor: '#FF9F64',
  },
  buttonText: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
  },
  yesText: {
    color: '#FFFFFF',
  },
  noText: {
    color: '#FFFFFF',
  },
});
